import nacl from 'tweetnacl';
import naclUtil from 'tweetnacl-util';

import { RECOVERY_RECEIPT_VERIFY_KEY } from '@/config/recoveryCanary';
import type {
  RecoveryIdentity,
  RecoveryReceiptVerification,
  RecoveryReceiptVerifier,
  RemoteRecoveryReceipt,
} from '@/types/recovery';

const verifiedReceipts = new WeakSet<RemoteRecoveryReceipt>();

const canonicalize = (value: unknown): unknown => {
  if (Array.isArray(value)) return value.map(canonicalize);
  if (value && typeof value === 'object') {
    return Object.keys(value as Record<string, unknown>)
      .sort()
      .reduce<Record<string, unknown>>((result, key) => {
        const item = (value as Record<string, unknown>)[key];
        if (item !== undefined) result[key] = canonicalize(item);
        return result;
      }, {});
  }
  return value;
};

export const canonicalRecoveryAttestation = (value: unknown): string =>
  JSON.stringify(canonicalize(value));

const identityMatches = (
  receipt: RemoteRecoveryReceipt,
  expected: { identity: RecoveryIdentity; recoveryId: string; manifestSha512: string }
): boolean => {
  const payload = receipt.attestationPayload;
  return (
    receipt.recoveryId === expected.recoveryId &&
    receipt.companyId === expected.identity.companyId &&
    receipt.userId === expected.identity.userId &&
    receipt.deviceId === expected.identity.deviceId &&
    receipt.manifestSha512 === expected.manifestSha512 &&
    payload.recovery.recoveryId === expected.recoveryId &&
    payload.recovery.companyId === expected.identity.companyId &&
    payload.recovery.userId === expected.identity.userId &&
    payload.recovery.deviceId === expected.identity.deviceId &&
    payload.manifest.ciphertextSha512 === expected.manifestSha512 &&
    payload.validatorKeyId === receipt.authority &&
    payload.validatedAt === receipt.validatedAt
  );
};

export class Ed25519RecoveryReceiptVerifier implements RecoveryReceiptVerifier {
  constructor(
    private readonly publicKeyBase64: string = RECOVERY_RECEIPT_VERIFY_KEY,
    private readonly now: () => Date = () => new Date()
  ) {}

  async verify(
    receipt: RemoteRecoveryReceipt,
    expected: { identity: RecoveryIdentity; recoveryId: string; manifestSha512: string }
  ): Promise<RecoveryReceiptVerification> {
    const verifiedAt = this.now().toISOString();
    const reject = (reason: string): RecoveryReceiptVerification => ({
      valid: false,
      verifiedAt,
      authority: receipt.authority,
      proofDigest: '',
      reason,
    });

    try {
      if (
        receipt.schema !== 'app-guincho-recovery-receipt/v1' ||
        receipt.status !== 'validated' ||
        receipt.attestationPayload?.schema !== 'app-guincho-recovery-attestation/v1' ||
        !identityMatches(receipt, expected)
      ) {
        return reject('receipt_identity_mismatch');
      }
      const publicKey = naclUtil.decodeBase64(this.publicKeyBase64.trim());
      const signature = naclUtil.decodeBase64(receipt.proof);
      if (publicKey.length !== nacl.sign.publicKeyLength || signature.length !== nacl.sign.signatureLength) {
        return reject('receipt_key_or_signature_invalid');
      }
      const message = naclUtil.decodeUTF8(canonicalRecoveryAttestation(receipt.attestationPayload));
      if (!nacl.sign.detached.verify(message, signature, publicKey)) {
        return reject('receipt_signature_invalid');
      }
      verifiedReceipts.add(receipt);
      return {
        valid: true,
        verifiedAt,
        authority: receipt.authority,
        proofDigest: naclUtil.encodeBase64(nacl.hash(signature)),
      };
    } catch {
      return reject('receipt_verification_failed');
    }
  }
}

export const isRecoveryReceiptVerifiedInProcess = (
  receipt: RemoteRecoveryReceipt,
  expected: { identity: RecoveryIdentity; recoveryId: string; manifestSha512: string }
): boolean => verifiedReceipts.has(receipt) && identityMatches(receipt, expected);
